import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { CrewChange } from '../../types';
import Input from '../Form/Input';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';

interface CrewChangeFormData {
  employeeId: string;
  status: 'sign_on' | 'sign_off' | 'continue';
  changeDate: string;
  branchId: string;
  raftTaken: boolean;
  takenTaken: boolean;
  notes?: string;
}

interface CrewChangeFormProps {
  crewChange?: CrewChange;
  onSubmit: (data: Partial<CrewChange>) => void;
  onCancel: () => void;
}

const CrewChangeForm: React.FC<CrewChangeFormProps> = ({ 
  crewChange, 
  onSubmit, 
  onCancel 
}) => {
  // Fetch employees and branches from API - NO MOCK DATA
  const { data: employees = [] } = useEmployees({ populate: '*' });
  const { data: branches = [] } = useBranches({ populate: '*' });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<CrewChangeFormData>({
    defaultValues: crewChange ? {
      employeeId: crewChange.employeeId,
      status: crewChange.status,
      changeDate: crewChange.changeDate ? String(crewChange.changeDate).split('T')[0] : '',
      branchId: crewChange.branchId,
      raftTaken: crewChange.raftTaken,
      takenTaken: crewChange.takenTaken,
      notes: crewChange.notes
    } : {
      employeeId: '',
      status: 'sign_on',
      changeDate: new Date().toISOString().split('T')[0],
      branchId: '',
      raftTaken: false,
      takenTaken: false,
      notes: ''
    }
  });

  // Saved employee/branch ids may be numeric (old records) while the select
  // options use documentIds, and the options load async — re-apply the saved
  // values normalized to documentIds once the lists are available.
  useEffect(() => {
    if (!crewChange) return;
    if (employees.length > 0 && crewChange.employeeId) {
      const emp: any = employees.find(
        (e: any) => e.documentId === crewChange.employeeId || String(e.id) === String(crewChange.employeeId)
      );
      if (emp) setValue('employeeId', emp.documentId || String(emp.id));
    }
    if (branches.length > 0 && crewChange.branchId) {
      const br: any = branches.find(
        (b: any) => b.documentId === crewChange.branchId || String(b.id) === String(crewChange.branchId)
      );
      if (br) setValue('branchId', br.documentId || String(br.id));
    }
  }, [crewChange, employees, branches, setValue]);

  const onFormSubmit = (data: CrewChangeFormData) => {
    // Send only the form fields (the record's extra keys shouldn't leak into the payload)
    onSubmit({
      employeeId: data.employeeId,
      status: data.status,
      changeDate: data.changeDate,
      branchId: data.branchId,
      raftTaken: data.raftTaken,
      takenTaken: data.takenTaken,
      notes: data.notes,
    });
  };

  // Employee options from API - NO MOCK DATA
  const employeeOptions = useMemo(() => {
    if (!employees || employees.length === 0) return [];
    return employees.map((employee: any) => ({
      value: employee.documentId || employee.id?.toString() || '',
      label: employee.name || 'Unknown'
    })).filter((opt: any) => opt.value);
  }, [employees]);

  // Crew changes happen on vessels only — offices/HQ are hidden. A saved
  // non-vessel value on an old record is still shown so editing doesn't blank it.
  const branchOptions = useMemo(() => {
    if (!branches || branches.length === 0) return [];
    const savedId = crewChange?.branchId;
    return branches
      .filter((branch: any) =>
        branch.type === 'vessel' ||
        (savedId && (branch.documentId === savedId || String(branch.id) === String(savedId)))
      )
      .map((branch: any) => ({
        value: branch.documentId || branch.id?.toString() || '',
        label: branch.name || 'Unknown'
      }))
      .filter((opt: any) => opt.value);
  }, [branches, crewChange]);

  const statusOptions = [
    { value: 'sign_on', label: 'Sign On' },
    { value: 'sign_off', label: 'Sign Off' },
    { value: 'continue', label: 'Continue' }
  ];

  const selectedStatus = watch('status');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {crewChange ? 'Edit Crew Change' : 'Add New Crew Change'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="col-span-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">
                Crew Change Information
              </h3>
            </div>
            
            <Select
              label="Employee"
              {...register('employeeId', { required: 'Please select an employee' })}
              options={employeeOptions}
              error={errors.employeeId?.message}
              placeholder="Select employee..."
            />
            
            <Select
              label="Status"
              {...register('status', {
                onChange: (e) => {
                  // "Continue" means the crew member is still aboard — the
                  // immigration sign-on from the original sign_on still holds,
                  // so it is pre-checked (and stays removable by unchecking).
                  if (e.target.value === 'continue') {
                    setValue('takenTaken', true);
                    setValue('raftTaken', false);
                  }
                },
              })}
              options={statusOptions}
              error={errors.status?.message}
            />
            
            <Input
              label="Change Date"
              type="date"
              {...register('changeDate')}
              error={errors.changeDate?.message}
            />
            
            <Select
              label="Vessel"
              {...register('branchId', { required: 'Please select a vessel' })}
              options={branchOptions}
              error={errors.branchId?.message}
              placeholder="Select vessel..."
            />
            
            {/* Checkboxes */}
            <div className="col-span-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2">
                Additional Information
              </h3>
            </div>
            
            {/* Sign On and Sign Off can never be checked together */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('raftTaken', {
                  onChange: (e) => { if (e.target.checked) setValue('takenTaken', false); },
                })}
                className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                Immigration Sign Off
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('takenTaken', {
                  onChange: (e) => { if (e.target.checked) setValue('raftTaken', false); },
                })}
                className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                Immigration Sign On
              </label>
            </div>
            
            <div className="col-span-full">
              <TextArea
                label="Notes"
                {...register('notes')}
                error={errors.notes?.message}
                placeholder="Additional notes about the crew change"
                rows={3}
              />
            </div>
            
            {/* Status Information */}
            {selectedStatus && (
              <div className="col-span-full">
                <div className={`p-4 rounded-lg ${
                  selectedStatus === 'sign_on' ? 'bg-green-50 border border-green-200' :
                  selectedStatus === 'sign_off' ? 'bg-red-50 border border-red-200' :
                  'bg-yellow-50 border border-yellow-200'
                }`}>
                  <p className={`text-sm ${
                    selectedStatus === 'sign_on' ? 'text-green-700' :
                    selectedStatus === 'sign_off' ? 'text-red-700' :
                    'text-yellow-700'
                  }`}>
                    {selectedStatus === 'sign_on' && '✓ This will add the employee to the active crew list'}
                    {selectedStatus === 'sign_off' && '✗ This will remove the employee from the active crew list'}
                    {selectedStatus === 'continue' && '→ This will update the employee\'s current assignment'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : crewChange ? 'Update Crew Change' : 'Add Crew Change'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrewChangeForm;